import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Privacy Policy page — /privacy
 * Required by Google Play, Samsung Galaxy Store, Huawei AppGallery, Apple App Store.
 *
 * Auto-detects the user's UI language. Also offers an inline language switcher
 * so mainland-China users (Huawei Rule 7.1 compliance) or any other visitor can
 * explicitly pick their language regardless of the app's current locale.
 */

const RTL_LANGS = new Set(['ar', 'ur', 'fa']);

// Language switcher list — keep short native labels
const LANG_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'العربية' },
  { code: 'zh', label: '中文' },
  { code: 'fr', label: 'Français' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'fa', label: 'فارسی' },
  { code: 'ur', label: 'اردو' },
  { code: 'id', label: 'Indonesia' },
  { code: 'bn', label: 'বাংলা' },
];

const PrivacyPage = () => {
  const { i18n } = useTranslation();
  const [override, setOverride] = useState(null);
  const active = override || i18n.language || 'en';
  const lang = TRANSLATIONS[active] ? active : 'en';
  const isRTL = RTL_LANGS.has(lang);
  const t = TRANSLATIONS[lang];

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0B132B',
        color: '#E5E7EB',
        padding: '40px 20px',
        fontFamily: isRTL ? 'system-ui, -apple-system, "Segoe UI", Tahoma, sans-serif' : 'system-ui',
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="privacy-page"
    >
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <a
          href="/"
          style={{
            color: '#0EA5E9',
            textDecoration: 'none',
            fontSize: 14,
            display: 'inline-block',
            marginBottom: 20,
          }}
          data-testid="privacy-back-link"
        >
          {t.back}
        </a>

        {/* Language switcher */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            marginBottom: 28,
            padding: '12px 14px',
            background: 'rgba(255,255,255,0.03)',
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.06)',
          }}
          data-testid="privacy-lang-switcher"
        >
          {LANG_OPTIONS.map((opt) => (
            <button
              key={opt.code}
              onClick={() => setOverride(opt.code)}
              style={{
                background: opt.code === lang ? 'rgba(14,165,233,0.18)' : 'transparent',
                color: opt.code === lang ? '#0EA5E9' : '#94A3B8',
                border: '1px solid ' + (opt.code === lang ? 'rgba(14,165,233,0.5)' : 'rgba(255,255,255,0.08)'),
                borderRadius: 6,
                padding: '5px 10px',
                fontSize: 12,
                fontWeight: opt.code === lang ? 600 : 400,
                cursor: 'pointer',
              }}
              data-testid={`privacy-lang-${opt.code}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8, color: '#FFFFFF' }}>
          {t.title}
        </h1>
        <p style={{ fontSize: 13, color: '#94A3B8', marginBottom: 32 }}>
          {t.lastUpdated}
        </p>

        {t.sections.map((s, i) => (
          <section key={i} style={{ marginBottom: 28 }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 10, color: '#F1F5F9' }}>
              {s.heading}
            </h2>
            <p style={{ fontSize: 15, lineHeight: 1.75, color: '#CBD5E1', whiteSpace: 'pre-line' }}>
              {s.body}
            </p>
          </section>
        ))}

        <hr style={{ borderColor: 'rgba(255,255,255,0.08)', margin: '32px 0 20px' }} />

        <p style={{ fontSize: 13, color: '#94A3B8', lineHeight: 1.7 }}>
          {t.contact}
        </p>

        <p style={{ fontSize: 12, color: '#64748B', marginTop: 40 }}>
          © Falak · {new Date().getFullYear()} · Riadh Al-Taie
        </p>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Content — identical policy substance across every language.
// ─────────────────────────────────────────────────────────────────────────────

const TRANSLATIONS = {
  ar: {
    back: '← العودة إلى فلك',
    title: 'سياسة الخصوصية — فلك',
    lastUpdated: 'آخر تحديث: يونيو 2026',
    sections: [
      {
        heading: '1. مقدّمة',
        body: 'تطبيق فلك (Falak) هو ساعة فلكية ومرجع لمواقيت الصلاة وحسابات أطوار القمر. نحن نحترم خصوصيتك ونعتبرها أمانة. هذه السياسة تشرح بوضوح ما هي البيانات التي قد يستخدمها التطبيق ولماذا.',
      },
      {
        heading: '2. البيانات التي يستخدمها التطبيق',
        body: '• الموقع الجغرافي (Latitude / Longitude): يُستخدم لحساب مواقيت الصلاة وموقع الشمس وحسابات القمر بدقة. يُرسل إلى خادم فلك لحظياً لإجراء الحساب فقط، ولا يُحفظ في أي قاعدة بيانات.\n\n• إشعارات مواقيت الصلاة (اختيارية): إذا فعّلت «تنبيه والتطبيق مغلق» نحفظ على خادم فلك رمز الإشعار الخاص بجهازك، ولغة الإشعار، ومواقيت صلاتك للأيام العشرة القادمة فقط، لنرسل كل إشعار في وقته. قد تدلّ هذه المواقيت على منطقتك التقريبية. لا نحفظ موقعك ولا اسمك ولا بريدك. تصل الإشعارات عبر خدمة الإشعارات التابعة لمتصفح هاتفك. تُحذف المواقيت المنقضية تلقائياً خلال يومين، ويُحذف كل شيء فور إيقاف الخيار.\n\n• درجة الحرارة: نطلبها من خدمة Open-Meteo المجانية لعرضها فقط في زاوية الواجهة. لا نُسجّل أي طلب.\n\n• تعليقات المستخدمين الاختيارية: عند استخدامك زر "شاركنا رأيك" يتم إرسال ما كتبته (مع بريدك إذا أدخلته اختيارياً) إلى قاعدة بياناتنا لقراءته من قبل المطوّر فقط. لا يُشارك مع أي طرف ثالث.',
      },
      {
        heading: '3. ما لا نفعله',
        body: '• لا نتتبّع موقعك في الخلفية.\n• لا نبيع بياناتك لأي معلن أو طرف ثالث.\n• لا نستخدم ملفات تتبّع إعلانية (Ad Trackers).\n• لا نستخدم Google Analytics أو Facebook Pixel أو أي أداة تحليل سلوكية.\n• لا نطلب أذونات لا يحتاجها التطبيق فعلياً.',
      },
      {
        heading: '4. تخزين الإعدادات المحلي',
        body: 'بعض الإعدادات التي تختارها (المدينة، اللغة، طريقة حساب الصلاة، التعديلات اليدوية بالدقائق، نوع الساعة) تُحفظ في ذاكرة المتصفح المحلية على جهازك (localStorage) لتوفير راحتك عند الفتح التالي. هذه البيانات لا تغادر جهازك أبداً.',
      },
      {
        heading: '5. حقوق المستخدم',
        body: 'لك في أي وقت:\n• مسح ذاكرة المتصفح لإزالة كل التفضيلات المحفوظة محلياً.\n• طلب حذف أي تعليق سبق أن أرسلته عبر مراسلة البريد المذكور أدناه.\n• معرفة ما هي البيانات الموجودة عنك في قاعدة بياناتنا (سنرد خلال 7 أيام).',
      },
      {
        heading: '6. الأطفال',
        body: 'التطبيق مناسب لجميع الأعمار ولا يجمع بيانات قابلة للتعريف من الأطفال أو الكبار. كل ما يُحفظ هو محتوى التعليق الذي يكتبه المستخدم طوعاً، وبيانات إشعارات الصلاة لمن يفعّلها بنفسه (انظر البند 2).',
      },
      {
        heading: '7. التحديثات على السياسة',
        body: 'قد نُحدّث هذه السياسة عند إضافة ميزات جديدة. سيظهر تاريخ "آخر تحديث" في أعلى الصفحة. لا نُجري تغييرات تمسّ خصوصيتك دون إعلامك بوضوح داخل التطبيق.',
      },
    ],
    contact: 'لأي سؤال أو طلب يتعلّق بالخصوصية، تواصل عبر: riadhaltaie@gmail.com',
  },

  en: {
    back: '← Back to Falak',
    title: 'Privacy Policy — Falak',
    lastUpdated: 'Last updated: June 2026',
    sections: [
      {
        heading: '1. Introduction',
        body: 'Falak is an astronomical clock and reference for prayer times and lunar phase calculations. We respect your privacy and treat it as a trust. This policy explains clearly what data the app may use and why.',
      },
      {
        heading: '2. Data the app uses',
        body: '• Geographic location (latitude / longitude): used to calculate prayer times, sun position, and moon phases precisely. It is sent to the Falak server only momentarily to perform the calculation and is never stored in any database.\n\n• Prayer-time notifications (optional): if you turn on "Alerts when the app is closed", we store on the Falak server your device\'s notification token, the notification language, and your prayer times for the next ten days only, so we can send each alert on time. These times may indicate your approximate region. We do not store your location, name, or email. Notifications are delivered through your phone browser\'s push service. Past times are deleted automatically within two days, and everything is deleted as soon as you turn the option off.\n\n• Temperature: requested from the free Open-Meteo service solely to display in the UI corner. We do not log any request.\n\n• Optional user feedback: when you use the "Share Feedback" button, the text you write (and your email if you optionally provide it) is sent to our database to be read by the developer only. It is never shared with any third party.',
      },
      {
        heading: '3. What we never do',
        body: '• We never track your location in the background.\n• We never sell your data to advertisers or third parties.\n• We never use ad-tracking files (ad trackers).\n• We never use Google Analytics, Facebook Pixel, or any behavioural analytics tool.\n• We never request permissions the app does not actually need.',
      },
      {
        heading: '4. Local settings storage',
        body: 'Some preferences you choose (city, language, prayer calculation method, manual minute offsets, clock type) are saved in your browser\'s local storage (localStorage) on your device for convenience on your next visit. This data never leaves your device.',
      },
      {
        heading: '5. User rights',
        body: 'At any time you may:\n• Clear your browser storage to remove all locally saved preferences.\n• Request deletion of any feedback you previously sent by emailing the address below.\n• Ask what data we have about you in our database (we respond within 7 days).',
      },
      {
        heading: '6. Children',
        body: 'Falak is suitable for all ages and does not collect personally-identifying data from anyone, children or adults. The only data stored is the feedback text users voluntarily submit, and the prayer-notification data of users who choose to turn it on (see section 2).',
      },
      {
        heading: '7. Updates to this policy',
        body: 'We may update this policy when adding new features. The "Last updated" date at the top of the page will reflect the change. We do not make changes that affect your privacy without clearly notifying you inside the app.',
      },
    ],
    contact: 'For any question or request related to privacy, please contact: riadhaltaie@gmail.com',
  },

  zh: {
    back: '← 返回 Falak',
    title: '隐私政策 — Falak',
    lastUpdated: '最后更新：2026年6月',
    sections: [
      {
        heading: '一、引言',
        body: 'Falak 是一款天文时钟应用，为您提供礼拜时间和月相计算参考。我们尊重您的隐私，并将其视为一项信任。本政策明确说明应用可能使用的数据类型及其用途。',
      },
      {
        heading: '二、应用使用的数据',
        body: '• 地理位置（经纬度）：用于精确计算礼拜时间、太阳位置和月相。仅为完成计算而临时发送到 Falak 服务器，不会存储在任何数据库中。\n\n• 礼拜时间通知（可选）：如果您开启"应用关闭时提醒"，我们会在 Falak 服务器上仅保存您设备的通知令牌、通知语言以及未来十天的礼拜时间，以便按时发送提醒。这些时间可能反映您的大致地区。我们不保存您的位置、姓名或电子邮件。通知通过您手机浏览器的推送服务送达。已过去的时间会在两天内自动删除，关闭该选项后所有数据立即删除。\n\n• 温度：向免费的 Open-Meteo 服务请求，仅用于在界面角落显示。我们不记录任何请求。\n\n• 可选的用户反馈：当您使用"分享反馈"按钮时，您输入的文字（以及您可选提供的电子邮件）将发送到我们的数据库，仅供开发者查阅。绝不会与任何第三方共享。',
      },
      {
        heading: '三、我们绝不会做的事',
        body: '• 我们绝不会在后台跟踪您的位置。\n• 我们绝不会将您的数据出售给广告商或第三方。\n• 我们绝不使用广告跟踪文件（广告追踪器）。\n• 我们绝不使用 Google Analytics、Facebook Pixel 或任何行为分析工具。\n• 我们绝不请求应用实际上不需要的权限。',
      },
      {
        heading: '四、本地设置存储',
        body: '您选择的一些偏好设置（城市、语言、礼拜计算方法、手动分钟偏移、时钟类型）会保存在您设备的浏览器本地存储（localStorage）中，以便您下次访问时使用。这些数据绝不会离开您的设备。',
      },
      {
        heading: '五、用户权利',
        body: '您可以随时：\n• 清除浏览器存储以删除所有本地保存的偏好设置。\n• 通过发送电子邮件至下方地址，请求删除您之前提交的任何反馈。\n• 询问我们数据库中关于您的数据（我们将在7天内回复）。',
      },
      {
        heading: '六、儿童',
        body: 'Falak 适合所有年龄段用户使用，不会从任何人（儿童或成人）收集个人身份识别数据。唯一存储的数据是用户自愿提交的反馈文本，以及主动开启礼拜通知的用户的通知数据（见第二条）。',
      },
      {
        heading: '七、政策更新',
        body: '当我们添加新功能时，可能会更新本政策。页面顶部的"最后更新"日期将反映更改。我们不会在不通过应用内明确通知您的情况下作出影响您隐私的更改。',
      },
    ],
    contact: '如有任何与隐私相关的问题或请求，请通过以下电子邮箱联系我们：riadhaltaie@gmail.com',
  },

  fr: {
    back: '← Retour à Falak',
    title: 'Politique de Confidentialité — Falak',
    lastUpdated: 'Dernière mise à jour : juin 2026',
    sections: [
      {
        heading: '1. Introduction',
        body: 'Falak est une horloge astronomique et une référence pour les horaires de prière et les calculs des phases lunaires. Nous respectons votre vie privée et la considérons comme un engagement de confiance. Cette politique explique clairement les données que l\'application peut utiliser et pourquoi.',
      },
      {
        heading: '2. Données utilisées par l\'application',
        body: '• Position géographique (latitude / longitude) : utilisée pour calculer avec précision les horaires de prière, la position du soleil et les phases lunaires. Elle est envoyée au serveur de Falak uniquement le temps du calcul et n\'est stockée dans aucune base de données.\n\n• Notifications des heures de prière (facultatives) : si vous activez « Alertes application fermée », nous conservons sur le serveur de Falak uniquement le jeton de notification de votre appareil, la langue des notifications et vos horaires de prière des dix prochains jours, afin d\'envoyer chaque alerte à l\'heure. Ces horaires peuvent indiquer votre région approximative. Nous ne conservons ni votre position, ni votre nom, ni votre e-mail. Les notifications sont acheminées par le service push du navigateur de votre téléphone. Les horaires passés sont supprimés automatiquement sous deux jours, et tout est supprimé dès que vous désactivez l\'option.\n\n• Température : demandée au service gratuit Open-Meteo uniquement pour affichage dans un coin de l\'interface. Nous n\'enregistrons aucune requête.\n\n• Commentaires facultatifs des utilisateurs : lorsque vous utilisez le bouton "Partager un commentaire", le texte que vous écrivez (avec votre e-mail si vous le fournissez à titre facultatif) est envoyé à notre base de données pour être lu uniquement par le développeur. Il n\'est jamais partagé avec un tiers.',
      },
      {
        heading: '3. Ce que nous ne faisons jamais',
        body: '• Nous ne suivons jamais votre position en arrière-plan.\n• Nous ne vendons jamais vos données à des annonceurs ou à des tiers.\n• Nous n\'utilisons jamais de fichiers de suivi publicitaire (traqueurs).\n• Nous n\'utilisons jamais Google Analytics, Facebook Pixel ni aucun outil d\'analyse comportementale.\n• Nous ne demandons jamais d\'autorisations dont l\'application n\'a pas réellement besoin.',
      },
      {
        heading: '4. Stockage local des paramètres',
        body: 'Certaines préférences que vous choisissez (ville, langue, méthode de calcul des prières, décalages manuels en minutes, type d\'horloge) sont enregistrées dans le stockage local de votre navigateur (localStorage) sur votre appareil pour votre commodité lors de votre prochaine visite. Ces données ne quittent jamais votre appareil.',
      },
      {
        heading: '5. Droits de l\'utilisateur',
        body: 'À tout moment, vous pouvez :\n• Vider le stockage de votre navigateur pour supprimer toutes les préférences enregistrées localement.\n• Demander la suppression de tout commentaire que vous avez précédemment envoyé en écrivant à l\'adresse ci-dessous.\n• Demander quelles données nous avons sur vous dans notre base de données (nous répondons sous 7 jours).',
      },
      {
        heading: '6. Enfants',
        body: 'Falak convient à tous les âges et ne collecte aucune donnée personnellement identifiable, ni auprès des enfants ni des adultes. Les seules données stockées sont le texte des commentaires que les utilisateurs soumettent volontairement, ainsi que les données de notification de prière des utilisateurs qui choisissent de les activer (voir section 2).',
      },
      {
        heading: '7. Mises à jour de cette politique',
        body: 'Nous pouvons mettre à jour cette politique lors de l\'ajout de nouvelles fonctionnalités. La date de "Dernière mise à jour" en haut de la page reflétera le changement. Nous n\'apportons pas de modifications affectant votre vie privée sans vous en informer clairement dans l\'application.',
      },
    ],
    contact: 'Pour toute question ou demande relative à la confidentialité, veuillez nous contacter à : riadhaltaie@gmail.com',
  },

  tr: {
    back: '← Falak\'a Dön',
    title: 'Gizlilik Politikası — Falak',
    lastUpdated: 'Son güncelleme: Haziran 2026',
    sections: [
      {
        heading: '1. Giriş',
        body: 'Falak, namaz vakitleri ve ay evresi hesaplamaları için bir astronomik saat ve referans uygulamasıdır. Gizliliğinize saygı duyar ve onu bir emanet olarak kabul ederiz. Bu politika, uygulamanın kullanabileceği verileri ve nedenlerini açıkça açıklar.',
      },
      {
        heading: '2. Uygulamanın kullandığı veriler',
        body: '• Coğrafi konum (enlem / boylam): namaz vakitlerini, güneş konumunu ve ay evrelerini hassas biçimde hesaplamak için kullanılır. Yalnızca hesaplama için anlık olarak Falak sunucusuna gönderilir ve hiçbir veritabanında saklanmaz.\n\n• Namaz vakti bildirimleri (isteğe bağlı): "Uygulama kapalıyken uyarı" seçeneğini açarsanız, her uyarıyı zamanında gönderebilmek için Falak sunucusunda yalnızca cihazınızın bildirim anahtarını, bildirim dilini ve önümüzdeki on günün namaz vakitlerini saklarız. Bu vakitler yaklaşık bölgenizi gösterebilir. Konumunuzu, adınızı veya e-postanızı saklamayız. Bildirimler telefon tarayıcınızın anlık bildirim hizmeti üzerinden iletilir. Geçmiş vakitler iki gün içinde otomatik silinir ve seçeneği kapattığınız anda her şey silinir.\n\n• Sıcaklık: yalnızca arayüzün bir köşesinde görüntülemek için ücretsiz Open-Meteo hizmetinden istenir. Hiçbir istek kaydetmiyoruz.\n\n• İsteğe bağlı kullanıcı geri bildirimi: "Geri Bildirim Paylaş" düğmesini kullandığınızda, yazdığınız metin (isteğe bağlı e-postanızla birlikte) yalnızca geliştirici tarafından okunmak üzere veritabanımıza gönderilir. Asla üçüncü taraflarla paylaşılmaz.',
      },
      {
        heading: '3. Asla yapmadıklarımız',
        body: '• Konumunuzu asla arka planda izlemiyoruz.\n• Verilerinizi asla reklamverenlere veya üçüncü taraflara satmıyoruz.\n• Reklam izleme dosyaları (reklam izleyicileri) asla kullanmıyoruz.\n• Google Analytics, Facebook Pixel veya herhangi bir davranışsal analiz aracı asla kullanmıyoruz.\n• Uygulamanın gerçekten ihtiyaç duymadığı izinleri asla istemiyoruz.',
      },
      {
        heading: '4. Yerel ayarların saklanması',
        body: 'Seçtiğiniz bazı tercihler (şehir, dil, namaz hesaplama yöntemi, manuel dakika ayarlamaları, saat türü), bir sonraki ziyaretinizde kolaylık sağlamak için cihazınızın tarayıcı yerel depolamasında (localStorage) saklanır. Bu veriler asla cihazınızdan çıkmaz.',
      },
      {
        heading: '5. Kullanıcı hakları',
        body: 'İstediğiniz zaman:\n• Yerel olarak kaydedilen tüm tercihleri kaldırmak için tarayıcı depolamanızı temizleyebilirsiniz.\n• Daha önce gönderdiğiniz herhangi bir geri bildirimin silinmesini aşağıdaki e-posta adresine yazarak talep edebilirsiniz.\n• Veritabanımızda size ait hangi verilerin bulunduğunu sorabilirsiniz (7 gün içinde yanıtlarız).',
      },
      {
        heading: '6. Çocuklar',
        body: 'Falak her yaş için uygundur ve çocuk veya yetişkin hiç kimseden kişisel olarak tanımlanabilir veri toplamaz. Saklanan veriler yalnızca kullanıcıların gönüllü olarak gönderdiği geri bildirim metni ve namaz bildirimlerini kendisi açan kullanıcıların bildirim verileridir (bkz. bölüm 2).',
      },
      {
        heading: '7. Bu politikanın güncellenmesi',
        body: 'Yeni özellikler eklendiğinde bu politikayı güncelleyebiliriz. Sayfanın üst kısmındaki "Son güncelleme" tarihi değişikliği yansıtacaktır. Uygulama içinde açıkça bilgilendirmeden gizliliğinizi etkileyen değişiklikler yapmıyoruz.',
      },
    ],
    contact: 'Gizlilikle ilgili herhangi bir soru veya talep için lütfen şu adresten bize ulaşın: riadhaltaie@gmail.com',
  },

  fa: {
    back: '← بازگشت به فلک',
    title: 'سیاست حریم خصوصی — فلک',
    lastUpdated: 'آخرین به‌روزرسانی: ژوئن ۲۰۲۶',
    sections: [
      {
        heading: '۱. مقدمه',
        body: 'فلک یک ساعت نجومی و مرجعی برای اوقات نماز و محاسبات فازهای ماه است. ما به حریم خصوصی شما احترام می‌گذاریم و آن را امانتی می‌دانیم. این سیاست به روشنی توضیح می‌دهد که برنامه چه داده‌هایی را ممکن است استفاده کند و چرا.',
      },
      {
        heading: '۲. داده‌هایی که برنامه استفاده می‌کند',
        body: '• موقعیت جغرافیایی (طول و عرض جغرافیایی): برای محاسبه‌ی دقیق اوقات نماز، موقعیت خورشید و فازهای ماه استفاده می‌شود. فقط برای انجام محاسبه به‌طور لحظه‌ای به سرور فلک ارسال می‌شود و در هیچ پایگاه داده‌ای ذخیره نمی‌گردد.\n\n• اعلان اوقات نماز (اختیاری): اگر «هشدار وقتی برنامه بسته است» را روشن کنید، فقط کد اعلان دستگاه شما، زبان اعلان و اوقات نماز ده روز آینده را روی سرور فلک نگه می‌داریم تا هر هشدار را به‌موقع بفرستیم. این اوقات ممکن است منطقه‌ی تقریبی شما را نشان دهد. موقعیت، نام یا ایمیل شما را ذخیره نمی‌کنیم. اعلان‌ها از طریق سرویس اعلان مرورگر گوشی شما تحویل داده می‌شوند. اوقات گذشته ظرف دو روز خودکار حذف می‌شوند و با خاموش کردن گزینه همه‌چیز فوراً پاک می‌شود.\n\n• دما: از سرویس رایگان Open-Meteo فقط برای نمایش در گوشه‌ی رابط درخواست می‌شود. هیچ درخواستی ثبت نمی‌کنیم.\n\n• بازخورد اختیاری کاربر: هنگام استفاده از دکمه‌ی "ارسال بازخورد"، متنی که می‌نویسید (و ایمیل اختیاری شما) به پایگاه داده‌ی ما ارسال می‌شود تا فقط توسط توسعه‌دهنده خوانده شود. هرگز با هیچ شخص ثالثی به اشتراک گذاشته نمی‌شود.',
      },
      {
        heading: '۳. کارهایی که هرگز انجام نمی‌دهیم',
        body: '• هرگز موقعیت شما را در پس‌زمینه ردیابی نمی‌کنیم.\n• هرگز داده‌های شما را به تبلیغ‌کنندگان یا اشخاص ثالث نمی‌فروشیم.\n• هرگز از فایل‌های ردیابی تبلیغاتی استفاده نمی‌کنیم.\n• هرگز از Google Analytics، Facebook Pixel یا هر ابزار تحلیل رفتاری استفاده نمی‌کنیم.\n• هرگز مجوزهایی را که برنامه واقعاً به آن نیاز ندارد درخواست نمی‌کنیم.',
      },
      {
        heading: '۴. ذخیره‌سازی محلی تنظیمات',
        body: 'برخی تنظیماتی که انتخاب می‌کنید (شهر، زبان، روش محاسبه‌ی نماز، تنظیمات دستی دقیقه، نوع ساعت) در فضای ذخیره‌سازی محلی مرورگر (localStorage) روی دستگاه شما ذخیره می‌شوند تا در بازدید بعدی شما راحت باشد. این داده‌ها هرگز دستگاه شما را ترک نمی‌کنند.',
      },
      {
        heading: '۵. حقوق کاربر',
        body: 'در هر زمان می‌توانید:\n• حافظه‌ی مرورگر خود را پاک کنید تا تمام تنظیمات ذخیره‌شده‌ی محلی حذف شوند.\n• حذف هر بازخوردی را که قبلاً ارسال کرده‌اید با ایمیل به آدرس زیر درخواست کنید.\n• بپرسید چه داده‌هایی درباره‌ی شما در پایگاه داده‌ی ما وجود دارد (ظرف ۷ روز پاسخ می‌دهیم).',
      },
      {
        heading: '۶. کودکان',
        body: 'فلک برای همه‌ی سنین مناسب است و هیچ داده‌ی قابل شناسایی شخصی از هیچ‌کس (کودک یا بزرگسال) جمع‌آوری نمی‌کند. تنها داده‌های ذخیره‌شده، متن بازخوردی است که کاربران داوطلبانه ارسال می‌کنند و داده‌های اعلان نماز کاربرانی که خودشان آن را روشن می‌کنند (بند ۲ را ببینید).',
      },
      {
        heading: '۷. به‌روزرسانی این سیاست',
        body: 'ممکن است هنگام افزودن ویژگی‌های جدید این سیاست را به‌روزرسانی کنیم. تاریخ "آخرین به‌روزرسانی" در بالای صفحه تغییر را منعکس خواهد کرد. بدون اطلاع‌رسانی روشن در برنامه، تغییراتی که بر حریم خصوصی شما تأثیر بگذارد ایجاد نمی‌کنیم.',
      },
    ],
    contact: 'برای هرگونه سؤال یا درخواست مرتبط با حریم خصوصی، لطفاً با ما تماس بگیرید: riadhaltaie@gmail.com',
  },

  ur: {
    back: '← فلک پر واپس جائیں',
    title: 'پرائیویسی پالیسی — فلک',
    lastUpdated: 'آخری بار اپ ڈیٹ: جون 2026',
    sections: [
      {
        heading: '1. تعارف',
        body: 'فلک ایک فلکیاتی گھڑی اور نماز کے اوقات اور چاند کے مرحلوں کے حسابات کے لیے ایک حوالہ ہے۔ ہم آپ کی پرائیویسی کا احترام کرتے ہیں اور اسے امانت سمجھتے ہیں۔ یہ پالیسی واضح طور پر بتاتی ہے کہ ایپ کون سا ڈیٹا استعمال کر سکتی ہے اور کیوں۔',
      },
      {
        heading: '2. ایپ جو ڈیٹا استعمال کرتی ہے',
        body: '• جغرافیائی مقام (طول البلد / عرض البلد): نماز کے اوقات، سورج کی پوزیشن اور چاند کے مرحلوں کے درست حساب کے لیے استعمال ہوتا ہے۔ صرف حساب کے لیے لمحاتی طور پر فلک کے سرور پر بھیجا جاتا ہے اور کسی ڈیٹا بیس میں محفوظ نہیں کیا جاتا۔\n\n• نماز کے اوقات کی اطلاعات (اختیاری): اگر آپ "ایپ بند ہونے پر اطلاع" آن کریں تو ہر اطلاع وقت پر بھیجنے کے لیے ہم فلک کے سرور پر صرف آپ کے آلے کا اطلاعاتی کوڈ، اطلاع کی زبان اور اگلے دس دنوں کے نماز کے اوقات محفوظ کرتے ہیں۔ یہ اوقات آپ کے تقریبی علاقے کی نشاندہی کر سکتے ہیں۔ ہم آپ کا مقام، نام یا ای میل محفوظ نہیں کرتے۔ اطلاعات آپ کے فون کے براؤزر کی پش سروس کے ذریعے پہنچتی ہیں۔ گزرے ہوئے اوقات دو دن میں خود بخود حذف ہو جاتے ہیں، اور اختیار بند کرتے ہی سب کچھ حذف ہو جاتا ہے۔\n\n• درجہ حرارت: صرف انٹرفیس کے کنارے پر دکھانے کے لیے مفت Open-Meteo سروس سے درخواست کی جاتی ہے۔ ہم کسی بھی درخواست کو ریکارڈ نہیں کرتے۔\n\n• اختیاری صارف کی رائے: جب آپ "رائے شیئر کریں" بٹن استعمال کرتے ہیں، آپ کا لکھا گیا متن (آپ کی اختیاری ای میل کے ساتھ) صرف ڈویلپر کے پڑھنے کے لیے ہمارے ڈیٹا بیس میں بھیجا جاتا ہے۔ اسے کبھی بھی کسی تیسرے فریق کے ساتھ شیئر نہیں کیا جاتا۔',
      },
      {
        heading: '3. جو ہم کبھی نہیں کرتے',
        body: '• ہم آپ کے مقام کو کبھی بھی پس منظر میں ٹریک نہیں کرتے۔\n• ہم آپ کا ڈیٹا اشتہار دہندگان یا فریق ثالث کو کبھی نہیں بیچتے۔\n• ہم اشتہاری ٹریکنگ فائلیں کبھی استعمال نہیں کرتے۔\n• ہم Google Analytics، Facebook Pixel یا کوئی طرزِ عمل تجزیاتی آلہ کبھی استعمال نہیں کرتے۔\n• ہم ایسی اجازتیں کبھی نہیں مانگتے جن کی ایپ کو واقعی ضرورت نہ ہو۔',
      },
      {
        heading: '4. مقامی ترتیبات کا اسٹوریج',
        body: 'کچھ ترجیحات جو آپ منتخب کرتے ہیں (شہر، زبان، نماز حساب کرنے کا طریقہ، دستی منٹ ایڈجسٹمنٹ، گھڑی کی قسم) آپ کے آلے پر آپ کے براؤزر کی مقامی اسٹوریج (localStorage) میں محفوظ ہوتی ہیں تاکہ اگلی بار آپ کو سہولت ہو۔ یہ ڈیٹا کبھی بھی آپ کے آلے سے باہر نہیں جاتا۔',
      },
      {
        heading: '5. صارف کے حقوق',
        body: 'آپ کسی بھی وقت:\n• اپنے براؤزر اسٹوریج کو صاف کر سکتے ہیں تاکہ تمام مقامی طور پر محفوظ ترجیحات ہٹا دی جائیں۔\n• کسی بھی رائے کو حذف کرنے کی درخواست کر سکتے ہیں جو آپ نے پہلے بھیجی ہے، نیچے دیے گئے پتے پر ای میل کر کے۔\n• پوچھ سکتے ہیں کہ ہمارے ڈیٹا بیس میں آپ کے بارے میں کون سا ڈیٹا موجود ہے (ہم 7 دنوں کے اندر جواب دیتے ہیں)۔',
      },
      {
        heading: '6. بچے',
        body: 'فلک ہر عمر کے لیے موزوں ہے اور کسی بھی بچے یا بالغ سے کوئی ذاتی طور پر شناختی ڈیٹا جمع نہیں کرتا۔ صرف ذخیرہ کیا گیا ڈیٹا وہ رائے کا متن ہے جو صارفین رضاکارانہ طور پر جمع کراتے ہیں، اور ان صارفین کا نماز کی اطلاعات کا ڈیٹا جو خود اسے آن کرتے ہیں (حصہ 2 دیکھیں)۔',
      },
      {
        heading: '7. اس پالیسی میں اپ ڈیٹس',
        body: 'جب نئی خصوصیات شامل کی جاتی ہیں تو ہم اس پالیسی کو اپ ڈیٹ کر سکتے ہیں۔ صفحے کے اوپر "آخری اپ ڈیٹ" کی تاریخ تبدیلی کی عکاسی کرے گی۔ ایپ کے اندر واضح طور پر آپ کو اطلاع دیے بغیر ہم آپ کی پرائیویسی پر اثر انداز ہونے والی تبدیلیاں نہیں کرتے۔',
      },
    ],
    contact: 'پرائیویسی سے متعلق کسی بھی سوال یا درخواست کے لیے، براہ کرم رابطہ کریں: riadhaltaie@gmail.com',
  },

  id: {
    back: '← Kembali ke Falak',
    title: 'Kebijakan Privasi — Falak',
    lastUpdated: 'Terakhir diperbarui: Juni 2026',
    sections: [
      {
        heading: '1. Pendahuluan',
        body: 'Falak adalah jam astronomi dan referensi untuk waktu salat dan perhitungan fase bulan. Kami menghormati privasi Anda dan menganggapnya sebagai amanah. Kebijakan ini menjelaskan secara jelas data apa yang mungkin digunakan aplikasi dan alasannya.',
      },
      {
        heading: '2. Data yang digunakan aplikasi',
        body: '• Lokasi geografis (lintang / bujur): digunakan untuk menghitung waktu salat, posisi matahari, dan fase bulan secara akurat. Dikirim ke server Falak hanya sesaat untuk perhitungan dan tidak disimpan di database mana pun.\n\n• Notifikasi waktu salat (opsional): jika Anda mengaktifkan "Pengingat saat aplikasi tertutup", kami hanya menyimpan token notifikasi perangkat Anda, bahasa notifikasi, dan waktu salat sepuluh hari ke depan di server Falak agar setiap pengingat terkirim tepat waktu. Waktu-waktu ini dapat menunjukkan perkiraan wilayah Anda. Kami tidak menyimpan lokasi, nama, atau email Anda. Notifikasi dikirim melalui layanan push peramban ponsel Anda. Waktu yang sudah lewat dihapus otomatis dalam dua hari, dan semuanya dihapus begitu Anda menonaktifkan opsi ini.\n\n• Suhu: diminta dari layanan gratis Open-Meteo hanya untuk ditampilkan di sudut antarmuka. Kami tidak mencatat permintaan apa pun.\n\n• Masukan pengguna opsional: saat Anda menggunakan tombol "Bagikan Masukan", teks yang Anda tulis (beserta email opsional Anda) dikirim ke database kami untuk dibaca hanya oleh pengembang. Tidak pernah dibagikan kepada pihak ketiga mana pun.',
      },
      {
        heading: '3. Yang tidak pernah kami lakukan',
        body: '• Kami tidak pernah melacak lokasi Anda di latar belakang.\n• Kami tidak pernah menjual data Anda kepada pengiklan atau pihak ketiga.\n• Kami tidak pernah menggunakan file pelacakan iklan.\n• Kami tidak pernah menggunakan Google Analytics, Facebook Pixel, atau alat analitik perilaku apa pun.\n• Kami tidak pernah meminta izin yang tidak benar-benar dibutuhkan aplikasi.',
      },
      {
        heading: '4. Penyimpanan pengaturan lokal',
        body: 'Beberapa preferensi yang Anda pilih (kota, bahasa, metode perhitungan salat, penyesuaian menit manual, jenis jam) disimpan di penyimpanan lokal browser (localStorage) di perangkat Anda untuk kenyamanan kunjungan berikutnya. Data ini tidak pernah meninggalkan perangkat Anda.',
      },
      {
        heading: '5. Hak pengguna',
        body: 'Kapan saja Anda dapat:\n• Menghapus penyimpanan browser Anda untuk menghapus semua preferensi yang tersimpan secara lokal.\n• Meminta penghapusan masukan apa pun yang sebelumnya Anda kirim dengan mengirim email ke alamat di bawah.\n• Menanyakan data apa yang kami miliki tentang Anda di database kami (kami merespons dalam 7 hari).',
      },
      {
        heading: '6. Anak-anak',
        body: 'Falak cocok untuk segala usia dan tidak mengumpulkan data pengenal pribadi dari siapa pun, anak-anak maupun dewasa. Data yang disimpan hanyalah teks masukan yang dikirimkan pengguna secara sukarela, serta data notifikasi salat dari pengguna yang memilih mengaktifkannya (lihat bagian 2).',
      },
      {
        heading: '7. Pembaruan kebijakan ini',
        body: 'Kami dapat memperbarui kebijakan ini saat menambahkan fitur baru. Tanggal "Terakhir diperbarui" di bagian atas halaman akan mencerminkan perubahan tersebut. Kami tidak membuat perubahan yang memengaruhi privasi Anda tanpa memberi tahu Anda dengan jelas di dalam aplikasi.',
      },
    ],
    contact: 'Untuk pertanyaan atau permintaan apa pun terkait privasi, silakan hubungi: riadhaltaie@gmail.com',
  },

  bn: {
    back: '← ফলকে ফিরে যান',
    title: 'গোপনীয়তা নীতি — ফলক',
    lastUpdated: 'সর্বশেষ আপডেট: জুন ২০২৬',
    sections: [
      {
        heading: '১. ভূমিকা',
        body: 'ফলক একটি জ্যোতির্বৈজ্ঞানিক ঘড়ি এবং সালাতের সময় ও চন্দ্র পর্যায়ের গণনার জন্য একটি রেফারেন্স। আমরা আপনার গোপনীয়তাকে সম্মান করি এবং এটিকে একটি আমানত হিসাবে বিবেচনা করি। এই নীতি স্পষ্টভাবে ব্যাখ্যা করে যে অ্যাপটি কী ডেটা ব্যবহার করতে পারে এবং কেন।',
      },
      {
        heading: '২. অ্যাপ যে ডেটা ব্যবহার করে',
        body: '• ভৌগোলিক অবস্থান (অক্ষাংশ / দ্রাঘিমাংশ): সঠিকভাবে সালাতের সময়, সূর্যের অবস্থান এবং চন্দ্র পর্যায় গণনা করতে ব্যবহৃত হয়। শুধু গণনার জন্য মুহূর্তের জন্য ফলক সার্ভারে পাঠানো হয় এবং কোনো ডাটাবেসে সংরক্ষণ করা হয় না।\n\n• নামাজের সময়ের বিজ্ঞপ্তি (ঐচ্ছিক): আপনি "অ্যাপ বন্ধ থাকলেও সতর্কতা" চালু করলে, প্রতিটি সতর্কতা সময়মতো পাঠাতে আমরা ফলক সার্ভারে শুধু আপনার ডিভাইসের বিজ্ঞপ্তি টোকেন, বিজ্ঞপ্তির ভাষা এবং পরবর্তী দশ দিনের নামাজের সময় সংরক্ষণ করি। এই সময়গুলো আপনার আনুমানিক এলাকা নির্দেশ করতে পারে। আমরা আপনার অবস্থান, নাম বা ইমেইল সংরক্ষণ করি না। বিজ্ঞপ্তি আপনার ফোনের ব্রাউজারের পুশ সেবার মাধ্যমে পৌঁছায়। পেরিয়ে যাওয়া সময় দুই দিনের মধ্যে স্বয়ংক্রিয়ভাবে মুছে যায়, এবং বিকল্পটি বন্ধ করলেই সবকিছু মুছে ফেলা হয়।\n\n• তাপমাত্রা: শুধুমাত্র UI কোণে প্রদর্শনের জন্য বিনামূল্যের Open-Meteo সেবা থেকে অনুরোধ করা হয়। আমরা কোনো অনুরোধ লগ করি না।\n\n• ঐচ্ছিক ব্যবহারকারীর মতামত: যখন আপনি "মতামত পাঠান" বোতাম ব্যবহার করেন, আপনার লেখা টেক্সট (এবং আপনার ঐচ্ছিক ইমেইল) শুধুমাত্র ডেভেলপার দ্বারা পড়ার জন্য আমাদের ডাটাবেসে পাঠানো হয়। এটি কখনোই কোনো তৃতীয় পক্ষের সাথে শেয়ার করা হয় না।',
      },
      {
        heading: '৩. আমরা যা কখনো করি না',
        body: '• আমরা কখনোই ব্যাকগ্রাউন্ডে আপনার অবস্থান ট্র্যাক করি না।\n• আমরা কখনোই আপনার ডেটা বিজ্ঞাপনদাতা বা তৃতীয় পক্ষের কাছে বিক্রি করি না।\n• আমরা কখনোই বিজ্ঞাপন ট্র্যাকিং ফাইল ব্যবহার করি না।\n• আমরা কখনোই Google Analytics, Facebook Pixel বা কোনো আচরণগত বিশ্লেষণ টুল ব্যবহার করি না।\n• আমরা কখনোই এমন অনুমতি চাই না যা অ্যাপের প্রকৃতপক্ষে প্রয়োজন নেই।',
      },
      {
        heading: '৪. স্থানীয় সেটিংস সংরক্ষণ',
        body: 'আপনি যে কিছু পছন্দ বেছে নেন (শহর, ভাষা, সালাত গণনার পদ্ধতি, ম্যানুয়াল মিনিট অফসেট, ঘড়ির ধরন) পরবর্তী পরিদর্শনে সুবিধার জন্য আপনার ডিভাইসের ব্রাউজারের স্থানীয় স্টোরেজে (localStorage) সংরক্ষিত হয়। এই ডেটা কখনোই আপনার ডিভাইস ছাড়ে না।',
      },
      {
        heading: '৫. ব্যবহারকারীর অধিকার',
        body: 'যেকোনো সময় আপনি:\n• স্থানীয়ভাবে সংরক্ষিত সমস্ত পছন্দ সরাতে আপনার ব্রাউজার স্টোরেজ পরিষ্কার করতে পারেন।\n• নিচের ঠিকানায় ইমেইল করে আপনি আগে পাঠানো যেকোনো মতামত মুছে ফেলার অনুরোধ করতে পারেন।\n• জিজ্ঞাসা করতে পারেন আমাদের ডাটাবেসে আপনার সম্পর্কে কী ডেটা আছে (আমরা ৭ দিনের মধ্যে সাড়া দিই)।',
      },
      {
        heading: '৬. শিশু',
        body: 'ফলক সকল বয়সের জন্য উপযুক্ত এবং কোনো শিশু বা প্রাপ্তবয়স্ক থেকে ব্যক্তিগত-শনাক্তকারী ডেটা সংগ্রহ করে না। সংরক্ষিত ডেটা শুধু ব্যবহারকারীরা স্বেচ্ছায় জমা দেওয়া মতামতের টেক্সট, এবং যারা নিজে চালু করেন তাদের নামাজের বিজ্ঞপ্তির ডেটা (অংশ ২ দেখুন)।',
      },
      {
        heading: '৭. এই নীতির আপডেট',
        body: 'নতুন বৈশিষ্ট্য যোগ করার সময় আমরা এই নীতি আপডেট করতে পারি। পৃষ্ঠার উপরে "সর্বশেষ আপডেট" তারিখটি পরিবর্তনটি প্রতিফলিত করবে। অ্যাপের ভিতরে আপনাকে স্পষ্টভাবে অবহিত না করে আমরা আপনার গোপনীয়তাকে প্রভাবিত করে এমন পরিবর্তন করি না।',
      },
    ],
    contact: 'গোপনীয়তা সম্পর্কিত যেকোনো প্রশ্ন বা অনুরোধের জন্য অনুগ্রহ করে যোগাযোগ করুন: riadhaltaie@gmail.com',
  },
};

export default PrivacyPage;
